import axios from "../../axios";
import { SetUserDetails, UserDetails } from "../../../store/reducer/slices/userReducer";

// Explicitly define the URL to avoid initialization race conditions
const URL = "/v2/user";

// Restore missing interface for paymentsApi
export interface PaymentDetailsAPIResponse {
  mode: string;
  details: string;
}

/**
 * Fetch the latest user details from the backend API
 */
export const getUserDetails = async () => {
  const result = await axios.get(URL);
  console.log("📥 Raw User Details from API:", result.data.user);
  const user: UserDetails = result.data.user;

  // Unpack any JSON encoded fields
  const jsonFields = [
    'links', 'skills', 'languages', 'hobbies', 'education', 
    'work_experience', 'achievements', 'community_engagement', 
    'testimonials', 'publications', 'security_clearance', 
    'tools_used', 'email_preferences', 'payment_details'
  ];

  jsonFields.forEach(field => {
    if ((user as any)[field] && typeof (user as any)[field] === "string") {
      try {
        (user as any)[field] = JSON.parse((user as any)[field]);
      } catch (e) {
        // If it fails to parse, it might already be an object or a legacy string
      }
    }
  });

  // Alias pfp/banner for the UI components
  user.profilePicture = user.pfp;
  user.bannerPicture = user.banner as string;

  // Map DB column two_fa_enabled → frontend field otpEnabled
  user.otpEnabled = !!(user as any).two_fa_enabled;
  user.totpSecret = (user as any).totp_secret;

  // Set paymentDetails alias if payment_details was parsed
  if (user.payment_details) {
    user.paymentDetails = user.payment_details;
  }

  return user;
};

/**
 * Admin version of get user details
 */
export const getUserDetailsAdmin = async (user_id: number | string) => {
  const result = await axios.get(URL + `/?id=${user_id}`);
  return result.data.user;
};

/**
 * Make a request to update the
 * current users details
 */
export const postUserDetails = async (
  details: { username: string } & SetUserDetails
) => {
  const formData = new FormData();
  
  // Create a clean copy for JSON stringification
  const cleanDetails = { ...details };
  delete cleanDetails.profilePicture;
  delete cleanDetails.bannerPicture;

  const payload = {
    ...cleanDetails,
    email_notifications_enabled: details.email_notifications_enabled,
    email_preferences: details.email_preferences,
    links: details.links ? JSON.stringify(details.links) : undefined,
    skills: details.skills ? JSON.stringify(details.skills) : undefined,
    languages: details.languages ? JSON.stringify(details.languages) : undefined,
    hobbies: details.hobbies ? JSON.stringify(details.hobbies) : undefined,
    achievements: details.achievements ? JSON.stringify(details.achievements) : undefined,
    education: details.education ? JSON.stringify(details.education) : undefined,
    work_experience: details.work_experience ? JSON.stringify(details.work_experience) : undefined,
    community_engagement: details.community_engagement ? JSON.stringify(details.community_engagement) : undefined,
    testimonials: details.testimonials ? JSON.stringify(details.testimonials) : undefined,
    publications: details.publications ? JSON.stringify(details.publications) : undefined,
    security_clearance: details.security_clearance ? JSON.stringify(details.security_clearance) : undefined,
    tools_used: details.tools_used ? JSON.stringify(details.tools_used) : undefined
  };

  formData.append("data", JSON.stringify(payload));

  if (details.profilePicture) formData.append("pfp", details.profilePicture);
  if (details.bannerPicture) formData.append("banner", details.bannerPicture);

  console.log("🚀 Sending User Update via Custom Axios:", payload);

  await axios.post(URL + "/details", formData);
  
  const result = { ...details };
  delete (result as any).profilePicture;
  delete (result as any).bannerPicture;
  return result;
};

/**
 * Change the users password
 */
export const changePassword = async (current: string, next: string) => {
  return await axios.post(URL + "/password", { current, new: next });
};

/**
 * Enable/disable two factor authentication
 */
export const requestAuthenticationChange = async (enabled: boolean, code?: string) => {
  // Backend accepts `status` and `code` fields; returns { message } on success
  await axios.post(URL + "/two-factor-auth", { status: enabled, code });
  // Return the intended value so the Redux reducer can update state correctly
  return enabled;
};

/**
 * Request OTP to disable two-factor authentication
 */
export const requestDisableOTP = async () => {
  await axios.post(URL + "/two-factor-auth/otp");
};

/**
 * Parse raw payment details (needed by paymentsApi)
 */
export const parsePaymentDetails = (details: any) => {
  return typeof details === "string" ? JSON.parse(details) : details;
};

/**
 * Setup TOTP: fetches QR code and backup codes from backend
 */
export const setupTOTPApi = async () => {
  const result = await axios.get(URL + "/totp/setup");
  return result.data;
};

/**
 * Enable TOTP: confirms code and enables TOTP MFA on the account
 */
export const enableTOTPApi = async (code: string, setupToken: string) => {
  const result = await axios.post(URL + "/totp/enable", { code, setupToken });
  return result.data;
};

/**
 * Disable TOTP: disables TOTP MFA on the account
 */
export const disableTOTPApi = async (code: string) => {
  const result = await axios.post(URL + "/totp/disable", { code });
  return result.data;
};

